import React from 'react';
import { FiSearch } from 'react-icons/fi';
import ViewButton from '../common/ViewButton';

function getTypeColor(type) {
  const map = {
    document: 'bg-[#edeef3] text-[#3a4a66]',
    resolution: 'bg-[#edeef3] text-[#3a4a66]',
    minutes: 'text-[#145f5f]',
  };
  return map[type] || 'bg-[#f2eee6] text-[#8a7d66]';
}

function getSourceColor(source) {
  const map = {
    ai: 'text-[#145f5f]',
    fallback: 'bg-[#f3ebd7] text-[#8a6a1f]',
    preview: 'bg-[#f2eee6] text-[#8a7d66]',
    'user-edited': 'bg-[#edeef3] text-[#3a4a66]',
  };
  return map[source] || 'bg-[#f2eee6] text-[#8a7d66]';
}

function OrdersTab({
  orders: drafts,
  loading,
  pagination,
  search,
  typeFilter,
  sourceFilter,
  dateFrom,
  dateTo,
  onSearchChange,
  onTypeFilterChange,
  onSourceFilterChange,
  onDateFromChange,
  onDateToChange,
  onRefresh,
  onPageChange,
  onViewOrder: onViewDraft,
}) {
  if (loading && (!drafts || drafts.length === 0)) {
    return (
      <div className="text-center py-20 text-[#7a7060]">
        <div className="animate-pulse">Loading drafts…</div>
      </div>
    );
  }

  return (
    <div>
      {/* Title + Refresh now live in AdminDashboardPage's top header. */}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex-1 min-w-[200px] relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7060] text-[13px]" />
          <input
            type="text"
            placeholder="Search by title, company, CIN..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 border-[1.5px] rounded-[9px] py-[9px] text-[12.5px] outline-none transition-shadow focus:shadow-sm focus:border-[#145f5f40]"
            style={{ borderColor: '#ddd6c6' }}
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value)}
          className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
          style={{ borderColor: '#ddd6c6' }}
        >
          <option value="">All Types</option>
          <option value="document">Document</option>
          <option value="resolution">Resolution</option>
          <option value="minutes">Minutes</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => onSourceFilterChange(e.target.value)}
          className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
          style={{ borderColor: '#ddd6c6' }}
        >
          <option value="">All Sources</option>
          <option value="ai">AI Generated</option>
          <option value="fallback">Fallback</option>
          <option value="preview">Preview</option>
          <option value="user-edited">User Edited</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => onDateFromChange(e.target.value)}
          className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
          style={{ borderColor: '#ddd6c6' }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => onDateToChange(e.target.value)}
          className="border-[1.5px] rounded-[9px] px-3 py-[9px] text-[12.5px] outline-none"
          style={{ borderColor: '#ddd6c6' }}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
        <div className="overflow-x-auto cs-table-scroll">
          <table className="w-full min-w-[1050px] text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#7a7060]" style={{ background: '#f8f5ef' }}>
                <th className="text-left py-3 px-4">Document</th>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">Source</th>
                <th className="text-left py-3 px-4">Company</th>
                <th className="text-left py-3 px-4">Created By</th>
                <th className="text-left py-3 px-4">Created</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => (
                <tr
                  key={draft._id}
                  onClick={() => onViewDraft(draft)}
                  className="border-b last:border-0 cursor-pointer transition-colors hover:bg-[#f8f5ef]"
                  style={{ borderColor: '#ddd6c6' }}
                >
                  <td className="py-3 px-4">
                    <div className="font-semibold text-[#08111f] max-w-[200px] truncate">{draft.documentTitle}</div>
                    <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{draft.documentId}</div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-semibold ${getTypeColor(draft.documentType)}`} style={draft.documentType === 'minutes' ? { background: '#145f5f22' } : {}}>
                      {draft.documentType}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-semibold ${getSourceColor(draft.docSource)}`} style={draft.docSource === 'ai' ? { background: '#145f5f22' } : {}}>
                      {draft.docSource}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-[#08111f] max-w-[150px] truncate">{draft.companyName || '-'}</div>
                    {draft.companyCin && <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{draft.companyCin}</div>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-[#08111f]">{draft.user?.fullName || '-'}</div>
                    <div className="text-[10px] text-[#7a7060]">{draft.user?.email}</div>
                  </td>
                  <td className="py-3 px-4 text-[#7a7060]">
                    {new Date(draft.createdAt).toLocaleDateString('en-IN')}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-semibold ${draft.isDeleted ? 'bg-[#f2e9e6] text-[#9a564a]' : 'bg-[#e8efe5] text-[#4a6b3f]'}`}>
                      {draft.isDeleted ? 'Deleted' : 'Active'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <ViewButton onClick={(e) => { e.stopPropagation(); onViewDraft(draft); }} title="View order" />
                  </td>
                </tr>
              ))}
              {drafts.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-[#7a7060]">No drafts found matching your criteria</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[12px] text-[#7a7060]">
            Page {pagination.current} of {pagination.pages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <button
              disabled={pagination.current <= 1}
              onClick={() => onPageChange(pagination.current - 1)}
              className="px-3 py-1.5 rounded-[9px] border text-[12px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]"
              style={{ borderColor: '#ddd6c6' }}
            >
              &larr; Prev
            </button>
            <button
              disabled={pagination.current >= pagination.pages}
              onClick={() => onPageChange(pagination.current + 1)}
              className="px-3 py-1.5 rounded-[9px] border text-[12px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]"
              style={{ borderColor: '#ddd6c6' }}
            >
              Next &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersTab;
